import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import AppSelect from 'src/components/common/AppSelect';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { DynamicFieldArray } from 'src/components/common/DynamicFieldArray';
import { AsyncSingleSelectDropdown } from 'src/components/common/AsyncSingleSelectDropdown';
import { LocationService } from 'src/services/api/LocationService';

const Field = ({ label, hint, children }) => (
  <div className="block">
    <div className="flex items-end justify-between">
      <div className="text-sm font-semibold text-slate-800">{label}</div>
      {hint ? <div className="text-xs text-slate-500">{hint}</div> : null}
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

const JobPostingsModal = ({
  open,
  onClose,
  requirements = [],
  onPublish,
  onSaveDraft,
}) => {
  const createLocationRow = (
    locationValue = null,
    employmentTypeValue = ''
  ) => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    location: locationValue,
    employmentType: employmentTypeValue,
  });

  const defaultDynamicFields = {
    skills: [{ value: '', isMandatory: true }],
    certifications: [{ value: '', isMandatory: true }],
    education_level: '',
    fieldOfStudy: [{ value: '', isMandatory: false }],
  };

  const {
    control,
    reset: resetDynamicFields,
    getValues,
  } = useForm({
    defaultValues: defaultDynamicFields,
  });

  const [selectedReqId, setSelectedReqId] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    closingDate: '',
    locations: [createLocationRow()],
  });

  const fetchLocations = useCallback(async ({ page, pageSize, search }) => {
    try {
      const resolvedPageSize = page > 1 ? 20 : pageSize;
      const response = await LocationService.listLocations({
        loadAll: true,
        page,
        pageSize: resolvedPageSize,
      });
      const locations = response?.data?.locations || [];
      let filteredLocations = locations;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredLocations = locations.filter((location) =>
          location.value?.toLowerCase().includes(searchLower)
        );
      }
      return {
        items: filteredLocations,
        hasNext: (locations?.length || 0) >= resolvedPageSize,
      };
    } catch (error) {
      console.error('Error fetching locations:', error);
      return { items: [], hasNext: false };
    }
  }, []);

  const skillOptions = useMemo(() => {
    const unique = new Set();
    requirements.forEach((req) => {
      (req.skills || []).forEach((skill) => unique.add(skill));
    });
    return Array.from(unique).map((skill) => ({
      value: skill,
      label: skill,
    }));
  }, [requirements]);

  const certificationOptions = useMemo(() => {
    const unique = new Set();
    requirements.forEach((req) => {
      (req.certifications || []).forEach((cert) => unique.add(cert));
    });
    return Array.from(unique).map((cert) => ({
      value: cert,
      label: cert,
    }));
  }, [requirements]);

  const educationLevelOptions = useMemo(
    () => [
      { value: 'DEGREE', label: 'Degree' },
      { value: 'MASTERS', label: 'Masters' },
    ],
    []
  );

  const fieldOfStudyOptions = useMemo(
    () => [
      { value: 'COMPUTER_SCIENCE', label: 'Computer Science' },
      { value: 'ENGINEERING', label: 'Engineering' },
      { value: 'BUSINESS', label: 'Business' },
      { value: 'FINANCE', label: 'Finance' },
      { value: 'MARKETING', label: 'Marketing' },
    ],
    []
  );

  const resetForm = () => {
    setSelectedReqId('');
    setForm({
      title: '',
      description: '',
      closingDate: '',
      locations: [createLocationRow()],
    });
    resetDynamicFields(defaultDynamicFields);
  };

  useEffect(() => {
    if (open) resetForm();
  }, [open]);

  const applyRequirement = (req) => {
    setForm({
      title: req.job_title || '',
      description: req.description || '',
      closingDate: req.closing_date || '',
      locations: Array.isArray(req.locations)
        ? req.locations.map((location) =>
            createLocationRow(
              { id: location, value: location },
              req.emp_type || ''
            )
          )
        : [createLocationRow(null, req.emp_type || '')],
    });
    resetDynamicFields({
      skills: Array.isArray(req.skills)
        ? req.skills.map((skill) => ({ value: skill, isMandatory: true }))
        : [{ value: '', isMandatory: true }],
      certifications: Array.isArray(req.certifications)
        ? req.certifications.map((cert) => ({ value: cert, isMandatory: true }))
        : [{ value: '', isMandatory: true }],
      education_level: '',
      fieldOfStudy: [{ value: '', isMandatory: false }],
    });
  };

  const onSelectRequirement = (id) => {
    setSelectedReqId(id);
    const req = requirements.find((entry) => entry.id === id);
    if (req) applyRequirement(req);
  };

  const updateLocation = (index, value) => {
    setForm((prev) => {
      const next = [...(prev.locations || [])];
      next[index] = { ...next[index], location: value };
      return { ...prev, locations: next };
    });
  };

  const updateEmploymentType = (index, value) => {
    setForm((prev) => {
      const next = [...(prev.locations || [])];
      next[index] = { ...next[index], employmentType: value };
      return { ...prev, locations: next };
    });
  };

  const addLocationRow = () => {
    setForm((prev) => ({
      ...prev,
      locations: [...(prev.locations || []), createLocationRow()],
    }));
  };

  const removeLocationRow = (index) => {
    setForm((prev) => {
      const next = [...(prev.locations || [])];
      next.splice(index, 1);
      return {
        ...prev,
        locations: next.length ? next : [createLocationRow()],
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const skills = (getValues('skills') || [])
      .map((item) => item?.value)
      .filter(Boolean);
    const certifications = (getValues('certifications') || [])
      .map((item) => item?.value)
      .filter(Boolean);
    const educationLevel = getValues('education_level') || '';
    const fieldOfStudy = (getValues('fieldOfStudy') || [])
      .map((item) => item?.value)
      .filter(Boolean);
    onPublish?.({
      ...form,
      skills,
      certifications,
      educationLevel,
      fieldOfStudy,
    });
    onClose();
  };

  const handleSaveDraft = () => {
    const skills = (getValues('skills') || [])
      .map((item) => item?.value)
      .filter(Boolean);
    const certifications = (getValues('certifications') || [])
      .map((item) => item?.value)
      .filter(Boolean);
    const educationLevel = getValues('education_level') || '';
    const fieldOfStudy = (getValues('fieldOfStudy') || [])
      .map((item) => item?.value)
      .filter(Boolean);
    onSaveDraft?.({
      ...form,
      skills,
      certifications,
      educationLevel,
      fieldOfStudy,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl p-0">
        <DialogHeader className="border-b border-slate-100 px-6 py-5 text-left">
          <DialogTitle className="text-lg font-bold text-foreground-strong">
            Post a job
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[calc(90vh-140px)] overflow-y-auto overscroll-contain">
          <form onSubmit={handleSubmit} className="px-6">
            <div className="grid grid-cols-1 grid-row-gap-5">
              <div className="lg:col-span-1 mb-4">
                <div className="rounded-2xl border border-slate-200 bg-muted p-4">
                  <div className="text-sm font-extrabold text-foreground-strong">
                    Requirement
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Selecting a requirement will populate: title, description,
                    closing date, education, certifications, skills, locations,
                    and employment type.
                  </div>

                  <div className="mt-4">
                    <Field label="Requirement select" hint="Required">
                      <AppSelect
                        value={selectedReqId}
                        onValueChange={onSelectRequirement}
                        options={requirements.map((req) => ({
                          value: req.id,
                          label: req.name,
                        }))}
                        placeholder="Select a requirement…"
                        className="w-full"
                      />
                    </Field>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-extrabold text-foreground-strong">
                        Job details
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        These values are populated from requirement, but you can
                        update before publishing.
                      </div>
                    </div>
                    <div className="rounded-md bg-blue-50 px-3 py-2 text-xs font-bold text-blue-900 ring-1 ring-blue-100">
                      Auto-filled
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Job title" hint="Required">
                      <input
                        value={form.title}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm font-semibold text-foreground-strong outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
                        placeholder="e.g., Business Analyst"
                        required
                      />
                    </Field>

                    <Field label="Closing date" hint="Required">
                      <input
                        type="date"
                        value={form.closingDate}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            closingDate: event.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm font-semibold text-foreground-strong outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
                        required
                      />
                    </Field>

                    <div className="sm:col-span-2">
                      <Field label="Job description" hint="Required">
                        <textarea
                          value={form.description}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              description: event.target.value,
                            }))
                          }
                          className="min-h-[140px] w-full resize-y rounded-lg border border-input bg-card px-3 py-3 text-sm text-foreground-strong outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
                          placeholder="Paste or write the job description…"
                          required
                        />
                      </Field>
                    </div>

                    <div className="sm:col-span-2">
                      <Field label="Education level" hint="Required">
                        <Controller
                          name="education_level"
                          control={control}
                          render={({ field }) => (
                            <AppSelect
                              options={educationLevelOptions}
                              onValueChange={field.onChange}
                              value={field.value}
                              placeholder="Select Education Level"
                              className="w-full"
                            />
                          )}
                        />
                      </Field>
                    </div>

                    <div className="sm:col-span-2">
                      <DynamicFieldArray
                        control={control}
                        name="fieldOfStudy"
                        options={fieldOfStudyOptions}
                        label="Field of Study"
                        isOptional
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <DynamicFieldArray
                        control={control}
                        name="certifications"
                        options={certificationOptions}
                        label="Certifications"
                        isOptional
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <DynamicFieldArray
                        control={control}
                        name="skills"
                        options={skillOptions}
                        label="Skills"
                        isOptional
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <Field
                        label="Locations & employment type"
                        hint="Required"
                      >
                        <div className="space-y-3">
                          {(form.locations || []).map((entry, index) => (
                            <div
                              key={entry.id}
                              className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_auto] md:items-end"
                            >
                              <AsyncSingleSelectDropdown
                                fetcher={fetchLocations}
                                getOptionLabel={(location) =>
                                  location.value || 'Unknown'
                                }
                                getOptionValue={(location) => location.id}
                                value={entry.location}
                                onChange={(value) =>
                                  updateLocation(index, value)
                                }
                                placeholder="Select Location"
                                searchPlaceholder="Search locations..."
                                pageSize={4}
                                loadMoreMode="button"
                                loadMoreLabel="Load More"
                                valueDisplay="text"
                                showSelectionIndicator={false}
                                showClearButton={false}
                              />
                              <AppSelect
                                value={entry.employmentType}
                                onValueChange={(value) =>
                                  updateEmploymentType(index, value)
                                }
                                options={[
                                  { value: 'Full-time', label: 'Full-time' },
                                  { value: 'Part-time', label: 'Part-time' },
                                  { value: 'Contract', label: 'Contract' },
                                  { value: 'Internship', label: 'Internship' },
                                ]}
                                placeholder="Select Employment Type"
                                className="w-full"
                              />
                              <div className="flex items-center gap-2">
                                {form.locations.length > 1 ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeLocationRow(index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="addField"
                            size="sm"
                            onClick={addLocationRow}
                          >
                            <Plus className="h-4 w-4" />
                            Add Location
                          </Button>
                        </div>
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 mt-6 -mx-6 border-t border-border bg-card px-6 py-4">
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md border border-input bg-card px-4 py-2.5 text-sm font-semibold text-foreground-strong hover:bg-slate-50 dark:hover:bg-muted"
                    >
                      Cancel
                    </button>

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        className="rounded-md bg-muted px-4 py-2.5 text-sm font-semibold text-slate-800 ring-1 ring-slate-200 hover:bg-slate-100"
                        onClick={handleSaveDraft}
                      >
                        Save draft
                      </button>
                      <button
                        type="submit"
                        className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-deep focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        Publish job
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobPostingsModal;
